import * as SignalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import { RootState } from "../../../app/store";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import React from "react";
import { isEmpty, isUrl } from "../../../utils/Util";

import {
  DealerProfile,
  GetPaymentMethods,
  GetDealerPaymentMethods,
  TargetPayApi,
  formatUSPhoneNo,
  isValidUSPhoneNo,
} from "../../../utils/UtilTarget";
import { useAppSelector } from "../../../app/hooks";
import InputMask from "react-input-mask";
import { StyleCreator } from "../../../helpers/stylecreator";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import { isNullOrEmptyField } from "../../../helpers/helpers";

/* -------------------- Constants -------------------- */

const styleDone = {
  marginLeft: "10px",
  color: "green",
  fontWeight: "bold",
  fontSize: "15px",
};

const ReviewInfo =
  "Review and edit the information below. (Settings can't be changed later)";
const DEFAULT_LABEL = "ChooseOne";
const STATUS_VALID = "valid";

/* -------------------- Component -------------------- */

export default function OnBoarding() {
  const classes = StyleCreator.makeStyles(formStyles.all);
  const navigate = useNavigate();
  const theme = useTheme();

  const zipTimeoutRef = useRef<any>(null);
  const lastStateRef = useRef("");
  const lastZipRef = useRef("");
  const onboardDealerTokenRef = useRef<string | null>(null);
  const initDealerDataRef = useRef<any>(null);

  let dealerId = useAppSelector((state: RootState) => state.dealer.dealerId);
  const group_dealerId = localStorage.getItem("group_dealerId");
  if (!isEmpty(group_dealerId)) dealerId = group_dealerId;

  /* -------------------- State ---------------------- */

  const [signalRconnection, setSignalRConnection] = useState<any>(null);
  const [dealerData, setDealerData] = useState<any>([]);
  const [isValidOnboardToken, setIsValidOnboardToken] = useState(true);

  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [dealerPaymentMethods, setDealerPaymentMethods] = useState<any[]>([]);

  const [AcceptTC, setAcceptTC] = useState("Continue");

  const [isHostedOnboardingStart, setIsHostedOnboardingStart] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [isStateLoading, setIsStateLoading] = useState(false);

  const [showPopOver, setShowPopOver] = useState(false);
  const [anchorPopOver, setAnchorPopOver] = useState(null);

  const [confirmOnboarding, setConfirmOnboarding] = useState(false);
  const [onBoardingValue, setOnBoardingValue] = useState("");

  const [zipcodeLabel, setZipCodeLabel] = useState("Zip Code");
  const [stateLabel, setStateLabel] = useState("State");

  const [countryCities, setCountryCities] = useState<any[]>([]);
  const [countryStates, setCountryStates] = useState<any[]>([]);

  const [legalNameError, setLegalNameError] = useState("");
  const [dbaError, setDBAError] = useState("");

  const [websiteUrls, setWebsiteUrls] = useState<string[]>([""]);
  const [websiteUrlErrors, setWebsiteUrlErrors] = useState<string[]>([""]);
  const [onlinePresence, setOnlinePresence] = useState<boolean>(false);

  /* -------------------- Init -------------------- */

  useEffect(() => {
    lastStateRef.current = "";
    lastZipRef.current = "";
    initDealerDataRef.current = null;

    document.title = `Onboard a Customer | ${theme.browserTitle}`;
    onboardDealerTokenRef.current = localStorage.getItem("onboardDealer_token");

    const init = async () => {
      await getDealer(dealerId);
    };

    init();
  }, []);

  /* -------------------- Country change -------------------- */
  useEffect(() => {
    if (isEmpty(dealerData.countryCode)) return;

    if (dealerData.countryCode === "US") {
      setZipCodeLabel("Zip Code");
      setStateLabel("State *");
    } else if (dealerData.countryCode === "CA") {
      setZipCodeLabel("Postal Code");
      setStateLabel("Province *");
    }

    const fetchCountryData = async () => {
      await Promise.all([
        getCountryStates(dealerData.countryCode),
        getPaymentMethod(dealerData.countryCode),
      ]);
    };
    fetchCountryData();
  }, [dealerData.countryCode]);

  /* -------------------- SignalR -------------------- */

  useEffect(() => {
    if (!signalRconnection) return;

    if (signalRconnection.state !== SignalR.HubConnectionState.Connected) {
      console.log("SignalR connectionState.0: " + signalRconnection.state);
      signalRconnection
        .start()
        .then(() => {
          console.log("SignalR connectionState.1: " + signalRconnection.state);
          signalRconnection.on("newMessage", (message) => {
            getDealer(dealerId);
          });
        })
        .catch((error) => console.log(error));
    }
  }, [signalRconnection]);

  async function makeSignalRConnection(referenceNumber: any) {
    const ReferenceIdForClients = referenceNumber;
    const accessToken = localStorage.getItem("access_token");
    const negotiateRequestBody = { ReferenceIdForClients };
    const negotiateRequestHeader = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    };
    try {
      let url = `${process.env.REACT_APP_BASE_URL}Notification/Negotiate`;
      let negotiateResponse: any = await axios.post(
        url,
        negotiateRequestBody,
        negotiateRequestHeader,
      );
      console.log(negotiateResponse);
      let signalRConnection: any = new SignalR.HubConnectionBuilder()
        .withUrl(negotiateResponse.data.clientConnectionUrl, {
          accessTokenFactory: () => negotiateResponse.data.accessToken,
        })
        .withAutomaticReconnect()
        .build();
      setSignalRConnection(signalRConnection);
    } catch (error) {
      console.error("Error: SignalR negotiation failed: ", error);
    }
  }

  /* -------------------- API Helpers -------------------- */

  const getDealer = async (dealerId: number) => {
    try {
      if (isEmpty(dealerId)) dealerId = await DealerProfile("dealerId");
      if (isEmpty(dealerId)) return;

      const isValidToken = await validateOnboardDealerToken(dealerId);

      if (isValidToken === false) {
        setIsValidOnboardToken(false);
        navigate("/401");
        return;
      }

      const res = await TargetPayApi(
        `GetDealerSetup?dealerId=${dealerId}`,
        null,
        "get",
      );

      if (isEmpty(res)) return;

      let locationGroupId = localStorage.getItem("location_group_id");
      let locationGroupName = localStorage.getItem("location_group_name");

      // Safe, normalized dealer object
      const dealerResponse = {
        ...res,
        nameDBA: res.name,
        locationGroupId: locationGroupId,
        locationGroupName:
          locationGroupName === "null" || locationGroupName === undefined
            ? null
            : locationGroupName,
      };

      if (!initDealerDataRef.current) initDealerDataRef.current = { ...res };

      // Parallel dependent calls
      const [, , methods] = await Promise.all([
        getCountryStates(dealerResponse.countryCode),
        getCountryCities(
          dealerResponse.countryCode,
          dealerResponse.stateOrProvince,
        ),
        GetDealerPaymentMethods(
          dealerId,
          dealerResponse.storeId,
          dealerResponse.countryCode,
        ),
      ]);

      if (isEmpty(dealerResponse.legalEntityId)) {
        dealerResponse.accountHolderReference = "";
        dealerResponse.accountHolderDescription = "";
      } else {
        const legalName = await getLegalEntities(dealerResponse);
        if (legalName) {
          dealerResponse.name = legalName;
        }
      }

      if (isEmpty(dealerResponse.splitConfigurationId)) {
        dealerResponse.splitConfigurationId = DEFAULT_LABEL;
      }

      if (
        !isEmpty(dealerResponse.phone) &&
        !isValidUSPhoneNo(dealerResponse.phone)
      ) {
        dealerResponse.phone = formatUSPhoneNo(dealerResponse.phone);
      }

      // Apply values to state
      setDealerPaymentMethods(methods?.length ? methods : paymentMethods);
      setDealerData((prev) => ({
        ...prev,
        ...dealerResponse,
        stateOrProvince: dealerResponse.stateOrProvince,
      }));

      const loadedWebsiteUrls: string[] = isEmpty(dealerResponse.websiteUrls)
        ? []
        : dealerResponse.websiteUrls
            .split(";")
            .map((url: string) => url.trim())
            .filter((url: string) => !isEmpty(url));
      setWebsiteUrls(loadedWebsiteUrls.length ? loadedWebsiteUrls : [""]);
      setWebsiteUrlErrors(
        loadedWebsiteUrls.length ? loadedWebsiteUrls.map(() => "") : [""],
      );
      setOnlinePresence(!!dealerResponse.onlinePresence);

      if (
        !isEmpty(res.legalEntityId) &&
        signalRconnection == null &&
        res.verificationStatus !== "valid"
      ) {
        makeSignalRConnection(res.legalEntityId);
      }

      if (res.verificationStatus === "valid") {
        setAcceptTC("Terms & Conditions");
        setIsHostedOnboardingStart(false);

        if (
          res.verificationStatus !==
          initDealerDataRef.current.verificationStatus
        ) {
          localStorage.setItem("onboardDealer_token", "");
          toast.dismiss();
          toast.success(
            "Onboarding process completed. Please refresh the screen to see the changes.",
            {
              position: toast.POSITION.TOP_CENTER,
              autoClose: false,
              closeOnClick: true,
              style: { width: "365px" },
            },
          );
        }
      }

      return dealerResponse;
    } catch (error) {
      console.error("Error in GetDealer:", error);
      toast.error("Failed to load dealer setup.");
    }
  };

  const getPaymentMethod = async (value: string) => {
    const response = await GetPaymentMethods(value);
    setPaymentMethods(response ?? []);
  };

  const getLegalEntities = async (dealerData: any) => {
    const res = await TargetPayApi(
      `LegalEntities?legalEntityId=${dealerData.legalEntityId}`,
      null,
      "get",
    );

    if (!isEmpty(res?.organization?.legalName)) {
      return res.organization.legalName;
    }

    return null;
  };

  const getCountryCities = async (
    country = null,
    state = null,
    search = null,
  ) => {
    setIsProcessing(true);
    try {
      const obj = {
        query: "CountryCities",
        queryBody: {
          Country: country,
          State: state != null ? state : dealerData.stateOrProvince,
          Search: search,
        },
      };

      const res = await TargetPayApi(
        "query",
        JSON.stringify(obj),
        "post",
        "Everest",
      );

      lastStateRef.current = obj.queryBody.State;

      setCountryCities(res ?? []);
    } catch (err) {
      console.error("Error in getCountryCities:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const getCountryStates = async (countryCode: string) => {
    const obj = { query: "CountryStates", queryBody: { Country: countryCode } };

    const res = await TargetPayApi(
      "query",
      JSON.stringify(obj),
      "post",
      "Everest",
    );

    setCountryStates(res ?? []);
  };

  /* -------------------- Handlers -------------------- */

  async function onTextChange(value: any, key: any) {
    setLegalNameError("");
    setDBAError("");

    // Restrict only:
    // 1. Single double quote (")
    // 2. Two single-quotes typed together ('')
    const hasDoubleQuote = value.includes('"');
    const hasDoubleSingleQuotes = value.includes("''");

    // Fields that should restrict special characters
    const restrictedFields = [
      "accountHolderReference", // Legal Company Name
      "accountHolderDescription", // Doing Business As
    ];

    // Validation for Legal Company Name & Doing Business As
    if (restrictedFields.includes(key)) {
      if (hasDoubleQuote || hasDoubleSingleQuotes) {
        const errorMessage = `Double quotes (") and double single-quotes ('') are not allowed.`;

        // Set field-specific error
        if (key === "accountHolderReference") {
          setLegalNameError(errorMessage);
        } else if (key === "accountHolderDescription") {
          setDBAError(errorMessage);
        }

        // Stop updating state if input is invalid
        return;
      }
    }

    // Update DealerData state safely instead of direct mutation
    setDealerData((prev) => ({ ...prev, [key]: value }));

    if (key === "zipPostal" && value.length >= 5) {
      clearTimeout(zipTimeoutRef.current);
      zipTimeoutRef.current = setTimeout(() => {
        getZip(value);
      }, 400);
    }

    if (key === "stateOrProvince") {
      setIsStateLoading(true);
      await getCountryCities(dealerData.countryCode, value);
      setDealerData((prev) => ({
        ...prev,
        city: "",
      }));
      setIsStateLoading(false);
    }
  }

  function getSelectedCity(city: string) {
    if (isEmpty(city)) return "";

    const match = countryCities.find(
      (c) => c.PrimaryCity.toLowerCase() === city.toLowerCase(),
    );
    return match?.PrimaryCity || "";
  }

  async function getZip(zip: string) {
    setIsZipLoading(true);
    setIsProcessing(true);
    try {
      let zipCode = zip.replaceAll("_", "");
      if (zipCode.length < 5) return;

      let dashIndex = zipCode.indexOf("-");
      if (dashIndex > 0) zipCode = zipCode.substring(0, dashIndex);
      if (zipCode.length < 5) return;
      if (zipCode === lastZipRef.current) return;

      const obj = { query: "ZipCode", queryBody: { zip: zipCode } };

      const res = await TargetPayApi(
        "query",
        JSON.stringify(obj),
        "post",
        "Everest",
      );

      if (!isEmpty(res)) {
        lastZipRef.current = zipCode;

        if (res.State !== lastStateRef.current)
          await getCountryCities(res.Country, res.State);

        setDealerData((prev) => ({
          ...prev,
          city: res.PrimaryCity,
          stateOrProvince: res.State,
        }));
      }
    } catch (err) {
      console.error("Error in getZip:", err);
    } finally {
      setIsProcessing(false);
      setIsZipLoading(false);
    }
  }

  // Remove leading and trailing spaces from the complete string on blur event.
  const trimSpaces = (value: string, key: string) => {
    setDealerData((prev) => ({ ...prev, [key]: value.trim() }));
  };

  // Website URLs are optional, so an empty entry is not an error.
  function getWebsiteUrlError(url: string): string {
    if (isEmpty(url)) return "";
    if (!isUrl(url)) return "Invalid URL format";
    return "";
  }

  function formatAdyenResponse(obj: any): any {
    let myJSON = JSON.stringify(obj);
    myJSON = myJSON.replaceAll("Adyen", "[PerseusPay]");
    myJSON = myJSON.replaceAll("adyen", "[PerseusPay]");
    return JSON.parse(myJSON);
  }

  /* -------------------- Validation & Submit -------------------- */

  const validateOnboardDealerToken = async (dealerId: number) => {
    if (
      !onboardDealerTokenRef.current ||
      onboardDealerTokenRef.current === "null"
    )
      return;
    else {
      const payload: any = {
        DealerId: dealerId,
        Token: onboardDealerTokenRef.current,
      };

      const response = await TargetPayApi("ValidatePreOnBoardingLink", payload);

      return response;
    }
  };

  function checkData(): any {
    if (
      isEmpty(dealerData.splitConfigurationId) ||
      dealerData.splitConfigurationId === DEFAULT_LABEL
    ) {
      toast.error("Missing pricing model");
      return false;
    }
    let PaymentCodes = "";
    for (let i = 0; i < dealerPaymentMethods.length; i++) {
      PaymentCodes += dealerPaymentMethods[i].code + ",";
    }

    if (isNullOrEmptyField(dealerData.accountHolderReference)) {
      toast.error("Missing Legal Company Name");
      return false;
    }

    if (isNullOrEmptyField(dealerData.accountHolderDescription)) {
      toast.error("Missing Doing Business As");
      return false;
    }

    if (isNullOrEmptyField(dealerData.address)) {
      toast.error("Missing Address");
      return false;
    }

    if (isNullOrEmptyField(dealerData.countryCode)) {
      toast.error("Missing Country");
      return false;
    }

    if (isNullOrEmptyField(dealerData.zipPostal)) {
      if (dealerData.countryCode === "US") toast.error("Missing Zip Code");
      else if (dealerData.countryCode === "CA")
        toast.error("Missing Postal Code");
      return false;
    }

    if (isNullOrEmptyField(dealerData.city)) {
      toast.error("Missing City");
      return false;
    }

    if (isNullOrEmptyField(dealerData.stateOrProvince)) {
      if (dealerData.countryCode === "US") toast.error("Missing State");
      else toast.error("Missing Province");
      return false;
    }

    if (isNullOrEmptyField(dealerData.phone)) {
      toast.error("Missing phone number");
      return false;
    }

    if (onlinePresence) {
      const urls = websiteUrls.map((url) => url.trim());

      if (urls.every(isEmpty)) {
        toast.error(
          "Provide at least one website URL, or uncheck 'Dealer has business website'.",
        );
        return false;
      }

      for (const url of urls) {
        const error = getWebsiteUrlError(url);
        if (error) {
          toast.error(error);
          return false;
        }
      }
    }

    return true;
  }

  function CheckComplete(column: any): any {
    try {
      let val = dealerData[column];
      if (val == null || val === "") return "";
      if (column === "verificationStatus") {
        if (val === STATUS_VALID) return <>&#x2713;</>;
        return "";
      }
      if (val !== "") return <>&#x2713;</>;
    } catch (error) {
      return "";
    }
  }

  async function HostedOnBoarding() {
    const res = await TargetPayApi("CreateOnBoardingLink", dealerData);

    if (!isEmpty(res)) {
      window.open(res, "_blank");
    }

    const timer = setTimeout(() => {}, 5000);
    setIsHostedOnboardingStart(true);

    clearTimeout(timer);

    return;
  }

  async function InitiateOnBoarding(e: any) {
    toast.dismiss();
    setOnBoardingValue("");

    if (!checkData()) return;

    setConfirmOnboarding(true);
    setShowPopOver(true);
    setAnchorPopOver(e.currentTarget);
  }

  async function ProcessOnboarding(e: any) {
    if (!checkData()) return;

    if (onBoardingValue !== dealerData.dmsDealerId.toString()) {
      toast.error("Please type " + dealerData.dmsDealerId + " to continue", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false,
        closeOnClick: true,
      });
      return;
    }

    setConfirmOnboarding(false);
    setShowPopOver(false);

    toast.dismiss();
    setIsProcessing(true);

    const payload = {
      ...dealerData,
      paymentMethods: dealerPaymentMethods.map((m) => m.code).join(","),
      websiteUrls: onlinePresence
        ? websiteUrls.map((url) => url.trim()).filter((url) => !isEmpty(url))
        : [],
      onlinePresence,
    };

    var res = await TargetPayApi("OnBoardDealer", payload);

    setIsProcessing(false);

    if (isEmpty(res)) return;

    if (!isEmpty(res.errorDetail)) {
      setDealerData((prev) => ({
        ...prev,
        legalEntityId: res.legalEntityId,
        balanceAccountId: res.balanceAccountId,
        accountHolderId: res.accountHolderId,
        businessLineId: res.businessLineId,
      }));

      toast.error(res.errorDetail, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false,
        closeOnClick: true,
      });

      return;
    }

    setDealerData(res);

    if (res.error !== null && res.errror !== "") {
      setShowPopOver(true);
      setIsProcessing(false);
      return;
    }

    getDealer(dealerData.dealerId);
  }

  /* -------------------- Render -------------------- */

  return (
    <Box style={{ border: "0px solid red" }}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ padding: "0px", paddingRight: "0px" }}
      >
        {isEmpty(dealerData) && (
          <Box>
            Please wait. Processing.....
            <CircularProgress></CircularProgress>{" "}
          </Box>
        )}
      </Box>

      {!isEmpty(dealerData) &&
        (isEmpty(dealerData.industryCode) ||
          isEmpty(dealerData.splitConfigurationId) ||
          dealerData.splitConfigurationId === DEFAULT_LABEL ||
          isValidOnboardToken === false) && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ padding: "0px", paddingRight: "0px" }}
          >
            <Box>
              <Navigate to="/401" />
            </Box>
          </Box>
        )}

      {dealerData &&
        dealerData.dealerId != null &&
        !isEmpty(dealerData.industryCode) &&
        !isEmpty(dealerData.splitConfigurationId) &&
        dealerData.splitConfigurationId !== DEFAULT_LABEL && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ padding: "0px", paddingRight: "0px" }}
          >
            <table style={{ border: "0px solid red" }}>
              <tbody>
                <tr>
                  <td valign="top" style={{ border: "0px solid green" }}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      style={{ padding: "0px" }}
                    >
                      <Box style={{ display: "block" }}>
                        <Paper>
                          <Box
                            style={{
                              margin: "5px 5px 0px 15px",
                              border: "0px solid red",
                            }}
                          >
                            {dealerData != null && (
                              <Box style={{ fontSize: "small" }}>
                                <br />
                                <Box
                                  style={{
                                    fontSize: "small",
                                    color:
                                      !isEmpty(dealerData) &&
                                      isEmpty(dealerData.storeReference)
                                        ? "black"
                                        : "gray",
                                  }}
                                >
                                  <i>{ReviewInfo}</i>
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <TextField
                                    label="Legal Company Name"
                                    className={classes.root}
                                    variant="standard"
                                    disabled={
                                      !isEmpty(dealerData.storeReference)
                                    }
                                    fullWidth
                                    required
                                    id="outlined-required"
                                    value={dealerData.accountHolderReference}
                                    onChange={(e) =>
                                      onTextChange(
                                        e.target.value,
                                        "accountHolderReference",
                                      )
                                    }
                                    onBlur={(e) => {
                                      trimSpaces(
                                        e.target.value,
                                        "accountHolderReference",
                                      );
                                      setLegalNameError("");
                                    }}
                                    inputProps={{
                                      style: { fontSize: "small" },
                                    }}
                                  />
                                </Box>

                                {legalNameError && (
                                  <Box style={{ color: "red" }}>
                                    {legalNameError}
                                  </Box>
                                )}

                                <Box style={formStyles.fieldSpacer}>
                                  <TextField
                                    label="Doing Business As"
                                    className={classes.root}
                                    variant="standard"
                                    disabled={
                                      !isEmpty(dealerData.storeReference)
                                    }
                                    fullWidth
                                    required
                                    id="outlined-required"
                                    value={dealerData.accountHolderDescription}
                                    onChange={(e) =>
                                      onTextChange(
                                        e.target.value,
                                        "accountHolderDescription",
                                      )
                                    }
                                    onBlur={(e) => {
                                      trimSpaces(
                                        e.target.value,
                                        "accountHolderDescription",
                                      );
                                      setDBAError("");
                                    }}
                                    inputProps={{
                                      style: { fontSize: "small" },
                                    }}
                                  />
                                </Box>

                                <Box
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#666",
                                    marginTop: "4px",
                                    maxWidth: "550px",
                                  }}
                                >
                                  Customers will see this on their bank
                                  statement — enter your recognizable business
                                  name, DO NOT enter &apos;Same&apos; or
                                  &apos;N/A&apos;.
                                </Box>

                                {dbaError && (
                                  <Box style={{ color: "red" }}>{dbaError}</Box>
                                )}

                                <Box style={formStyles.fieldSpacer}>
                                  <TextField
                                    label="Address"
                                    variant="standard"
                                    disabled={
                                      !isEmpty(dealerData.storeReference)
                                    }
                                    style={{ marginTop: "5px" }}
                                    fullWidth
                                    required
                                    id="outlined-required"
                                    className={classes.root}
                                    value={dealerData.address}
                                    onChange={(e) =>
                                      onTextChange(e.target.value, "address")
                                    }
                                    onBlur={(e) => {
                                      trimSpaces(e.target.value, "address");
                                    }}
                                    inputProps={{
                                      style: { fontSize: "small" },
                                    }}
                                  />
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <FormControl>
                                    <InputLabel
                                      variant="standard"
                                      id="demo-simple-select-label"
                                      sx={{ fontSize: "large" }}
                                      style={{
                                        color: !isEmpty(
                                          dealerData.storeReference,
                                        )
                                          ? "rgba(0, 0, 0, 0.38)"
                                          : "rgba(0, 0, 0, 0.6)",
                                      }}
                                    >
                                      Country *
                                    </InputLabel>
                                    <Select
                                      disabled
                                      variant="standard"
                                      required
                                      sx={{ fontSize: "small", width: "550px" }}
                                      fullWidth
                                      labelId="demo-simple-select-label"
                                      id="demo-simple-select"
                                      value={dealerData.countryCode}
                                      label="Country"
                                      onChange={(e) =>
                                        setDealerData({
                                          ...dealerData,
                                          countryCode: e.target.value,
                                        })
                                      }
                                    >
                                      <MenuItem
                                        sx={{ fontSize: "small" }}
                                        value={"US"}
                                      >
                                        United States
                                      </MenuItem>
                                      <MenuItem
                                        sx={{ fontSize: "small" }}
                                        value={"CA"}
                                      >
                                        Canada
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <InputMask
                                    mask={
                                      dealerData.countryCode === "US"
                                        ? "99999"
                                        : "a9a 9a9"
                                    }
                                    alwaysShowMask={false}
                                    disabled={
                                      !isEmpty(dealerData.storeReference)
                                    }
                                    value={dealerData.zipPostal}
                                    onChange={(e) =>
                                      onTextChange(e.target.value, "zipPostal")
                                    }
                                  >
                                    {() => (
                                      <TextField
                                        disabled={
                                          !isEmpty(dealerData.storeReference)
                                        }
                                        label={zipcodeLabel}
                                        variant="standard"
                                        fullWidth
                                        required
                                        id="outlined-required"
                                        className={classes.maskTextField}
                                      />
                                    )}
                                  </InputMask>
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <FormControl>
                                    <InputLabel
                                      variant="standard"
                                      id="demo-simple-select-label"
                                      sx={{ fontSize: "large" }}
                                      style={{
                                        color: !isEmpty(
                                          dealerData.storeReference,
                                        )
                                          ? "rgba(0, 0, 0, 0.38)"
                                          : "rgba(0, 0, 0, 0.6)",
                                      }}
                                    >
                                      City *
                                    </InputLabel>
                                    <Select
                                      disabled={
                                        !isEmpty(dealerData.storeReference) ||
                                        isZipLoading ||
                                        isStateLoading
                                      }
                                      variant="standard"
                                      required
                                      sx={{ fontSize: "small", width: "550px" }}
                                      fullWidth
                                      labelId="demo-simple-select-label"
                                      id="demo-simple-select"
                                      value={getSelectedCity(dealerData.city)}
                                      onChange={(e) =>
                                        onTextChange(e.target.value, "city")
                                      }
                                    >
                                      {countryCities.map(
                                        (row: any, index: number) => {
                                          return (
                                            <MenuItem
                                              sx={{ fontSize: "small" }}
                                              value={row.PrimaryCity}
                                              key={index}
                                            >
                                              {row.PrimaryCity}
                                            </MenuItem>
                                          );
                                        },
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <FormControl>
                                    <InputLabel
                                      variant="standard"
                                      id="demo-simple-select-label"
                                      sx={{ fontSize: "large" }}
                                      style={{
                                        color: !isEmpty(
                                          dealerData.storeReference,
                                        )
                                          ? "rgba(0, 0, 0, 0.38)"
                                          : "rgba(0, 0, 0, 0.6)",
                                      }}
                                    >
                                      {stateLabel}
                                    </InputLabel>
                                    <Select
                                      disabled={
                                        !isEmpty(dealerData.storeReference) ||
                                        isZipLoading
                                      }
                                      variant="standard"
                                      required
                                      sx={{ fontSize: "small", width: "550px" }}
                                      fullWidth
                                      labelId="demo-simple-select-label"
                                      id="demo-simple-select"
                                      value={dealerData.stateOrProvince}
                                      onChange={(e) =>
                                        onTextChange(
                                          e.target.value,
                                          "stateOrProvince",
                                        )
                                      }
                                    >
                                      {countryStates.map(
                                        (row: any, index: number) => {
                                          return (
                                            <MenuItem
                                              sx={{ fontSize: "small" }}
                                              value={row.Code}
                                              key={index}
                                            >
                                              {row.Name}
                                            </MenuItem>
                                          );
                                        },
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <InputMask
                                    mask="+1 (999) 999 9999"
                                    alwaysShowMask={false}
                                    disabled={
                                      !isEmpty(dealerData.storeReference)
                                    }
                                    value={dealerData.phone}
                                    onChange={(e) =>
                                      onTextChange(e.target.value, "phone")
                                    }
                                  >
                                    {() => (
                                      <TextField
                                        disabled={
                                          !isEmpty(dealerData.storeReference)
                                        }
                                        label="Phone Number"
                                        variant="standard"
                                        fullWidth
                                        required
                                        id="outlined-required"
                                        className={classes.maskTextField}
                                      />
                                    )}
                                  </InputMask>
                                </Box>
                                <Box style={formStyles.fieldSpacer}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        style={{
                                          padding: "2px 5px",
                                          fontSize: "small",
                                        }}
                                        checked={onlinePresence}
                                        onChange={(e) => {
                                          setOnlinePresence(e.target.checked);
                                          setWebsiteUrls([""]);
                                          setWebsiteUrlErrors([""]);
                                        }}
                                        disabled={
                                          !isEmpty(dealerData.storeReference)
                                        }
                                      />
                                    }
                                    label={
                                      <span style={{ fontSize: "small" }}>
                                        Dealer has business website
                                      </span>
                                    }
                                  />
                                  {onlinePresence &&
                                    websiteUrls.map((url, index) => (
                                      <Box
                                        key={index}
                                        style={formStyles.fieldSpacer}
                                      >
                                        <Box
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <TextField
                                            disabled={
                                              !isEmpty(
                                                dealerData.storeReference,
                                              )
                                            }
                                            className={classes.root}
                                            variant="standard"
                                            fullWidth
                                            id="outlined-required"
                                            value={url}
                                            onChange={(e) => {
                                              const value =
                                                e.target.value.trim();
                                              setWebsiteUrls((prev) =>
                                                prev.map((u, i) =>
                                                  i === index ? value : u,
                                                ),
                                              );
                                              setWebsiteUrlErrors((prev) =>
                                                prev.map((err, i) =>
                                                  i === index
                                                    ? getWebsiteUrlError(value)
                                                    : err,
                                                ),
                                              );
                                            }}
                                            onBlur={(e) => {
                                              const trimmed =
                                                e.target.value.trim();
                                              setWebsiteUrls((prev) =>
                                                prev.map((u, i) =>
                                                  i === index ? trimmed : u,
                                                ),
                                              );
                                            }}
                                            inputProps={{
                                              style: { fontSize: "small" },
                                            }}
                                          />
                                          {websiteUrls.length > 1 &&
                                            isEmpty(
                                              dealerData.storeReference,
                                            ) && (
                                              <IconButton
                                                color="error"
                                                size="small"
                                                aria-label="Remove website URL"
                                                disabled={
                                                  !isEmpty(
                                                    dealerData.storeReference,
                                                  ) || isProcessing
                                                }
                                                onClick={() => {
                                                  setWebsiteUrls((prev) =>
                                                    prev.filter(
                                                      (_, i) => i !== index,
                                                    ),
                                                  );
                                                  setWebsiteUrlErrors((prev) =>
                                                    prev.filter(
                                                      (_, i) => i !== index,
                                                    ),
                                                  );
                                                }}
                                              >
                                                <RemoveCircleOutlineIcon fontSize="small" />
                                              </IconButton>
                                            )}
                                        </Box>
                                        {websiteUrlErrors[index] && (
                                          <Box style={{ color: "red" }}>
                                            {websiteUrlErrors[index]}
                                          </Box>
                                        )}
                                      </Box>
                                    ))}
                                  {onlinePresence &&
                                    isEmpty(dealerData.storeReference) && (
                                      <Button
                                        size="small"
                                        disabled={isProcessing}
                                        onClick={() => {
                                          setWebsiteUrls((prev) => [
                                            ...prev,
                                            "",
                                          ]);
                                          setWebsiteUrlErrors((prev) => [
                                            ...prev,
                                            "",
                                          ]);
                                        }}
                                      >
                                        + Add another URL
                                      </Button>
                                    )}
                                  {onlinePresence && (
                                    <Box
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#666",
                                        marginTop: "4px",
                                        maxWidth: "550px",
                                      }}
                                    >
                                      Enter your business website. If you have
                                      multiple websites, please enter them all.
                                      If you do not have a business website,
                                      uncheck the box above.
                                    </Box>
                                  )}
                                </Box>
                                <Box
                                  style={{
                                    padding: "20px 10px 10px 0px",
                                    textAlign: "left",
                                  }}
                                >
                                  <table>
                                    <tbody>
                                      <tr>
                                        <td>
                                          {dealerData &&
                                            (isEmpty(
                                              dealerData.storeReference,
                                            ) ||
                                              isEmpty(
                                                dealerData.paymentMethodsDateCreated,
                                              )) &&
                                            dealerData.splitConfigurationId !==
                                              DEFAULT_LABEL && (
                                              <Button
                                                style={{ marginRight: "10px" }}
                                                disabled={
                                                  showPopOver || isProcessing
                                                }
                                                variant="contained"
                                                onClick={(e) =>
                                                  InitiateOnBoarding(e)
                                                }
                                              >
                                                Initiate onboarding
                                              </Button>
                                            )}
                                        </td>
                                        <td>
                                          {isProcessing && (
                                            <CircularProgress
                                              size={25}
                                              style={{ marginLeft: "5px" }}
                                              color="info"
                                            />
                                          )}
                                        </td>
                                        <td>
                                          {!isEmpty(
                                            dealerData.storeReference,
                                          ) &&
                                            !isEmpty(
                                              dealerData.paymentMethodsDateCreated,
                                            ) &&
                                            !isProcessing && (
                                              <Button
                                                variant="contained"
                                                onClick={(e) =>
                                                  HostedOnBoarding()
                                                }
                                              >
                                                {AcceptTC}
                                              </Button>
                                            )}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </Box>
                              </Box>
                            )}
                          </Box>
                          <Box
                            style={{
                              border: "0px solid green",
                              padding: "5px",
                            }}
                          >
                            {dealerData.verificationProblems != null &&
                              dealerData.paymentMethodsDateCreated != null &&
                              isHostedOnboardingStart === true && (
                                <Box style={{ display: "table" }}>
                                  <Paper
                                    style={{
                                      margin: "0px 10px",
                                      padding: "10px",
                                      fontSize: "small",
                                      color: "red",
                                    }}
                                  >
                                    <h6>
                                      Click on the continue button to fix the
                                      errors below
                                    </h6>
                                    {dealerData.verificationProblems &&
                                      JSON.parse(
                                        dealerData.verificationProblems,
                                      ).map((row: any, index: number) => (
                                        <Box
                                          style={{ display: "table" }}
                                          key={index}
                                        >
                                          {row.verificationErrors.map(
                                            (err: any, idx: number) => (
                                              <Box
                                                style={{ display: "table" }}
                                                key={idx}
                                              >
                                                {err.code}: {err.message}
                                              </Box>
                                            ),
                                          )}
                                        </Box>
                                      ))}
                                  </Paper>
                                </Box>
                              )}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  </td>
                  <td valign="top" style={{ paddingTop: "5px" }}>
                    <Paper
                      style={{ margin: "0px 20px 0px 5px", marginLeft: "10px" }}
                    >
                      <Box
                        style={{
                          borderLeft: "0px solid silver",
                          whiteSpace: "nowrap",
                          fontSize: "small",
                          padding: "5px 10px",
                        }}
                      >
                        <Box>
                          Legal Entity
                          <span style={styleDone}>
                            {CheckComplete("legalEntityId")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Account Holder
                          <span style={styleDone}>
                            {CheckComplete("accountHolderId")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Balance Account
                          <span style={styleDone}>
                            {CheckComplete("balanceAccountId")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Business Line
                          <span style={styleDone}>
                            {CheckComplete("businessLineId")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Create Store
                          <span style={styleDone}>
                            {CheckComplete("storeId")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Payment Methods
                          <span style={styleDone}>
                            {CheckComplete("paymentMethodsDateCreated")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Terms & Conditions
                          <span style={styleDone}>
                            {CheckComplete("verificationStatus")}
                          </span>
                        </Box>
                        <Box style={{ padding: "4px 0px" }}>
                          Payout Schedule
                          <span style={styleDone}>
                            {CheckComplete("sweepId")}
                          </span>
                        </Box>
                      </Box>
                    </Paper>
                    {!isEmpty(dealerData.storeReference) &&
                      !isEmpty(dealerPaymentMethods) && (
                        <Paper style={{ margin: "10px 20px 0px 10px" }}>
                          {dealerPaymentMethods.map(
                            (row: any, index: number) => {
                              const imagePath = `/images/PM_${row.code}.png`;
                              return (
                                <React.Fragment key={index}>
                                  <img
                                    src={imagePath}
                                    className="paymentMethod-logo"
                                    key={index}
                                  />
                                  {(index + 1) % 3 === 0 && <br />}
                                </React.Fragment>
                              );
                            },
                          )}
                        </Paper>
                      )}
                  </td>
                </tr>
              </tbody>
            </table>
            <Popover
              style={{ marginTop: "15px" }}
              open={showPopOver}
              anchorEl={anchorPopOver}
              onClose={() => {
                setShowPopOver(false);
                setConfirmOnboarding(false);
                setOnBoardingValue("");
              }}
              anchorOrigin={{
                vertical: "center",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Box>
                <div
                  style={{
                    padding: "0px 0px",
                    minWidth: "200px",
                    minHeight: "100px",
                  }}
                >
                  {confirmOnboarding && (
                    <Box style={{ fontSize: "small" }}>
                      <Box
                        style={{
                          borderBottom: "1px solid silver",
                          padding: "3px 5px",
                          backgroundColor: "#F8F9F9",
                        }}
                      >
                        Confirm Onboarding
                      </Box>
                      <Box style={{ padding: "10px 20px" }}>
                        <table>
                          <tbody>
                            <tr>
                              <td>
                                Please type{" "}
                                <span style={{ fontWeight: "bold" }}>
                                  {dealerData.dmsDealerId}
                                </span>{" "}
                                to continue
                              </td>
                              <td>
                                <TextField
                                  autoComplete="off"
                                  autoFocus
                                  placeholder={dealerData.dmsDealerId.toString()}
                                  style={{
                                    marginTop: "5px",
                                    marginLeft: "10px",
                                  }}
                                  required
                                  id="outlined-required"
                                  className={classes.root}
                                  value={onBoardingValue}
                                  size="small"
                                  onChange={(e) =>
                                    setOnBoardingValue(e.target.value)
                                  }
                                  inputProps={{ style: { fontSize: "small" } }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <Box
                                  style={{
                                    textAlign: "right",
                                    padding: "10px",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    onClick={(e) => ProcessOnboarding(e)}
                                  >
                                    continue onboarding
                                  </Button>
                                  <Button
                                    style={{ marginLeft: "10px" }}
                                    variant="contained"
                                    onClick={(e) => setShowPopOver(false)}
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                  )}

                  {dealerData.adyenResponse && (
                    <Box>
                      <Box
                        style={{
                          borderBottom: "1px solid silver",
                          padding: "3px 5px",
                          backgroundColor: "#F8F9F9",
                          fontWeight: "bold",
                        }}
                      >
                        Error:
                      </Box>
                      <Box style={{ padding: "0px 10px" }}>
                        <pre>
                          {JSON.stringify(
                            formatAdyenResponse(dealerData.adyenResponse),
                            null,
                            2,
                          )}
                        </pre>
                      </Box>
                    </Box>
                  )}
                </div>
              </Box>
            </Popover>
          </Box>
        )}
    </Box>
  );
}
